import { useCallback, useMemo, useState } from 'react'

import { useForm, y } from '@circlefin/form'
import { useModal } from '@circlefin/modal-router'
import { RecipientAddresses } from '@containers/forms/Combobox'
import { useOnChainTransfer } from '@modals/blockchain'
import { routes } from '@services/sections/modal'
import { useConnectWallet } from '@shared/wallet/hooks/useConnectWallet'
import useTranslation from 'next-translate/useTranslation'

import { AceDepositInfoBanner } from '../../components'
import { ConnectRecipientWallet } from '../../containers'

import type { OnChainTransferDestination } from '@modals/blockchain'

const schema = y.object({
  /**
   * Destination blockchain address.
   */
  destination: y.mixed<OnChainTransferDestination>().required(),
})

type TransferRecipientFormValues = y.InferType<typeof schema>

export const ChooseRecipientForm: React.FC = () => {
  const { t } = useTranslation('ace')
  const { t: tCommon } = useTranslation('common')
  const { router } = useModal()

  const { isConnected } = useConnectWallet()
  const [values, { setTransferState }] = useOnChainTransfer()

  const [walletConnectError, setWalletConnectError] = useState(false)
  const blockchain = useMemo(() => values.blockchain, [values.blockchain])

  const [Form, { watch, trigger }] = useForm<TransferRecipientFormValues>({
    schema,
    defaultValues: {
      destination: values.destination,
    },
  })

  const destination = watch('destination')

  const connectWalletOnSuccess = useCallback(() => {
    void trigger('destination')
    setWalletConnectError(false)
  }, [trigger])

  const connectWalletOnError = useCallback(() => {
    void trigger('destination')
    setWalletConnectError(true)
  }, [trigger])

  /**
   * Handle Form onSubmit.
   */
  const handleFormOnSubmit = useCallback(
    ({ destination }: TransferRecipientFormValues) => {
      setTransferState({
        destination: destination,
        blockchain: destination.chain,
      })
      router.push(routes.transfer.wallet.selectAmount)
    },
    [router, setTransferState],
  )

  const hideSubmitButton = useMemo(() => {
    if (isConnected) {
      void trigger('destination')
    }
    return !isConnected || walletConnectError
  }, [isConnected, trigger, walletConnectError])

  return (
    <Form onSubmit={handleFormOnSubmit}>
      <RecipientAddresses
        blockchain={blockchain}
        label={t('deposit.chooseRecipient.label')}
        message={t('deposit.chooseRecipient.message')}
        name="destination"
        placeholder={t('deposit.chooseRecipient.placeholder')}
        hideDropdownHeader
      />

      <AceDepositInfoBanner blockchain={blockchain} className="mt-4" />

      {destination != null ? (
        <div className="mt-4 border-t border-black-50">
          <ConnectRecipientWallet
            address={destination.address}
            chain={destination.chain}
            label={t('deposit.chooseRecipient.connectedWalletLabel')}
            onError={connectWalletOnError}
            onSuccess={connectWalletOnSuccess}
          />
        </div>
      ) : null}

      {!hideSubmitButton && (
        <Form.SubmitButton className="mt-6 h-10 w-full" variant="primary">
          {tCommon('continue')}
        </Form.SubmitButton>
      )}
    </Form>
  )
}
